// components/tabs/AnalyticsTab.js
import React from 'react';
import { motion } from 'framer-motion';
import {Activity, Zap, Coffee, Moon, Users, Sparkles, Award, Shield, Heart, Gamepad2, Clock, Target, Flame, Trophy } from 'lucide-react';
import { SiGithub, SiX } from '@icons-pack/react-simple-icons';
import PremiumCard from '../ui/PremiumCard';
import PremiumContributionChart from '../ui/PremiumContributionChart';
import StreakCards from '../features/StreakCards';
import { formatNumber, formatDate } from '../../utils/formatters';

const AnalyticsTab = ({ userData, timeSeriesData, githubActivityTypes, xActivityTypes, userHistory }) => {
  // Get ALL points data from pointsStats/pointsBreakdown
  
type PointsData = Record<string, number | undefined>;
type ConsolidatedPoints = Record<string, number>;

const consolidatePoints = (
  pointsData: PointsData,
  totalAiPoints: number | undefined
): ConsolidatedPoints => {
  const consolidated: ConsolidatedPoints = {};

  // GitHub consolidation
  const githubTotal =
    (pointsData.github ?? 0) +
    (pointsData.githubInteraction ?? 0) +
    (pointsData.GitHub ?? 0);
  if (githubTotal > 0) consolidated.github = githubTotal;

  // X/Twitter consolidation
  const xTotal =
    (pointsData.x ?? 0) +
    (pointsData.tweet ?? 0) +
    (pointsData.xInteraction ?? 0) +
    (pointsData.X ?? 0) +
    (pointsData.twitter ?? 0);
  if (xTotal > 0) consolidated.x = xTotal;

  // AI consolidation
  const aiTotal =
    (totalAiPoints ?? 0) +
    (pointsData.ai ?? 0) +
    (pointsData.AIActivity ?? 0) +
    (pointsData.AI ?? 0);
  if (aiTotal > 0) consolidated.ai = aiTotal;

  // Add non-duplicate categories
  const skipKeys = new Set([
    'github',
    'githubInteraction',
    'GitHub',
    'x',
    'tweet',
    'xInteraction',
    'X',
    'twitter',
    'ai',
    'AIActivity',
    'AI',
  ]);

  for (const [key, value] of Object.entries(pointsData)) {
    if (!skipKeys.has(key) && typeof value === 'number' && value > 0) {
      consolidated[key] = value;
    }
  }
  return consolidated;
};

// 👇 Use the updated function
const pointsData = userData.pointsStats || userData.pointsBreakdown || {};
const consolidatedPoints = consolidatePoints(pointsData, userData.totalAiPoints);
  
  // Define categories with proper names and icons
  const categoryMetadata = {
    ai: { name: 'AI Interactions', color: '#FFB800', icon: Sparkles },
    github: { name: 'GitHub Activity', color: '#333333', icon: SiGithub },
    x: { name: 'X (Twitter)', color: '#1DA1F2', icon: SiX },
    gm: { name: 'GM Streaks', color: '#F59E0B', icon: Coffee },
    gn: { name: 'GN Streaks', color: '#6366F1', icon: Moon },
    sendit: { name: 'SendIt Activity', color: '#10B981', icon: Zap },
    role: { name: 'Role Rewards', color: '#EC4899', icon: Users },
    staking: { name: 'Staking Rewards', color: '#8B5CF6', icon: Shield },
    tip: { name: 'Tips & Rewards', color: '#14B8A6', icon: Heart },
    amVerified: { name: 'AM Verified', color: '#84CC16', icon: Shield },
    gmario: { name: 'GMario Game', color: '#F97316', icon: Gamepad2 }
  };
  
  // Build points breakdown from consolidated data
  const pointsBreakdown = Object.entries(consolidatedPoints)
    .map(([key, points]) => {
      const metadata = categoryMetadata[key] || {
        name: key.charAt(0).toUpperCase() + key.slice(1),
        color: '#6B7280',
        icon: Sparkles
      };
      
      return {
        category: metadata.name,
        points: points,
        color: metadata.color,
        icon: metadata.icon,
        key: key
      };
    })
    .sort((a, b) => b.points - a.points);

  const totalPoints = userData.points || 0;
  const totalCategorizedPoints = pointsBreakdown.reduce((sum, item) => sum + item.points, 0);

  // Calculate engagement metrics
  const engagementMetrics = {
    dailyAverage: Math.round(totalPoints / 30),
    bestStreak: Math.max(
      userData.streaks?.gm?.current || 0,
      userData.streaks?.gn?.current || 0,
      userData.streaks?.sendit?.current || 0
    ),
    totalActivities: userData.xPostingsCount + userData.githubInteractionsCount,
    activeCategories: pointsBreakdown.filter(p => p.points > 0).length
  };

  // Process points history for display
  const recentPointsHistory = userData.pointsHistory || userHistory || [];
  const displayHistory = recentPointsHistory.slice(0, 10);

  return (
    <div className="space-y-8">
      {/* Dynamic Stats Grid - Different from passport */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          className="col-span-2 lg:col-span-1"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="h-full p-6 bg-gradient-to-br from-[#15FF75]/10 to-[#2CF98E]/5 backdrop-blur-md rounded-2xl border border-[#15FF75]/20">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#15FF75]/20 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#15FF75]" />
              </div>
              <span className="text-xs text-[#15FF75] font-medium px-2 py-1 bg-[#15FF75]/10 rounded">TOTAL</span>
            </div>
            <div className="text-3xl font-bold text-[#E8FDF2] mb-1">{formatNumber(totalPoints)}</div>
            <p className="text-sm text-[#8AA5A2]">AmPoints Earned</p>
          </div>
        </motion.div>

        <motion.div
          className="relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <PremiumCard className="h-full p-6">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-[#FFB800]" />
                <span className="text-xs text-[#8AA5A2]">Daily Average</span>
              </div>
              <div className="text-2xl font-bold text-[#E8FDF2]">{formatNumber(engagementMetrics.dailyAverage)}</div>
              <div className="text-xs text-[#FFB800] mt-1">↑ Keep it up!</div>
            </div>
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-[#FFB800]/10 rounded-full blur-2xl"></div>
          </PremiumCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <PremiumCard className="h-full p-6 bg-gradient-to-br from-[#8B5CF6]/5 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-[#8B5CF6]" />
              <span className="text-xs text-[#8AA5A2]">Best Streak</span>
            </div>
            <div className="text-2xl font-bold text-[#E8FDF2]">{engagementMetrics.bestStreak} days</div>
            <div className="mt-2 h-1 bg-[#8B5CF6]/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#8B5CF6] rounded-full" style={{ width: `${Math.min(engagementMetrics.bestStreak * 3.33, 100)}%` }}></div>
            </div>
          </PremiumCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <PremiumCard className="h-full p-6 border-[#1DA1F2]/20">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-[#1DA1F2]" />
              <span className="text-xs text-[#8AA5A2]">Activities</span>
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold text-[#E8FDF2]">{formatNumber(engagementMetrics.totalActivities)}</div>
              <span className="text-xs text-[#1DA1F2]">total</span>
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-[#8AA5A2]">GitHub: {userData.githubInteractionsCount}</span>
              <span className="text-[#8AA5A2]">X: {userData.xPostingsCount}</span>
            </div>
          </PremiumCard>
        </motion.div>
      </div>

      {/* Achievement Streaks - Moved up */}
      <PremiumCard className="overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-[#E8FDF2] mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#FFB800]" />
            Achievement Streaks
          </h3>
          <StreakCards streaks={userData?.streaks} />
        </div>
      </PremiumCard>

      {/* Growth Charts - Side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PremiumCard>
          <PremiumContributionChart 
            data={timeSeriesData.github} 
            type="area" 
            title="GitHub Growth Trajectory"
            color="#333333"
          />
        </PremiumCard>

        <PremiumCard>
          <PremiumContributionChart 
            data={timeSeriesData.x} 
            type="area" 
            title="X Platform Growth Trajectory"
            color="#1DA1F2"
          />
        </PremiumCard>
      </div>

      {/* Points Breakdown - New Visual Design */}
      <PremiumCard className="p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-[#E8FDF2] flex items-center gap-2">
            <Award className="w-6 h-6 text-[#FFB800]" />
            Points Distribution
          </h3>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-[#8AA5A2]">Total: </span>
              <span className="font-bold text-[#15FF75]">{formatNumber(totalPoints)}</span>
            </div>
            <div className="text-sm">
              <span className="text-[#8AA5A2]">Categories: </span>
              <span className="font-bold text-[#E8FDF2]">{engagementMetrics.activeCategories}</span>
            </div>
          </div>
        </div>

        {/* Visual points breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top categories as cards */}
          <div className="space-y-3">
            {pointsBreakdown.slice(0, 5).map((item, index) => {
              const IconComponent = item.icon;
              const percentage = totalPoints > 0 ? (item.points / totalPoints * 100).toFixed(1) : '0.0';
              
              return (
                <motion.div
                  key={item.key}
                  className="relative p-4 rounded-xl border border-white/[0.08] hover:border-white/[0.12] 
                           bg-gradient-to-r from-[#0A0E1B]/60 to-transparent transition-all group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4 }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 
                               group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: item.color }} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-[#E8FDF2]">{item.category}</h4>
                        <span className="font-bold text-[#E8FDF2]">{formatNumber(item.points)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#0A0E1B] rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ backgroundColor: item.color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          />
                        </div>
                        <span className="text-xs text-[#8AA5A2] w-12 text-right">{percentage}%</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Circular visualization for all categories */}
          <div className="flex items-center justify-center p-8">
            <div className="relative w-48 h-48">
              {/* Center total */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-3xl font-bold text-[#15FF75]">{formatNumber(totalPoints)}</div>
                <div className="text-xs text-[#8AA5A2] uppercase">Total Points</div>
              </div>
              
              {/* Circular segments */}
              <svg className="w-full h-full transform -rotate-90">
                {(() => {
                  let cumulativePercentage = 0;
                  return pointsBreakdown.slice(0, 6).map((item, index) => {
                    const percentage = (item.points / totalPoints) * 100;
                    const strokeDasharray = `${percentage * 3.14} ${314 - percentage * 3.14}`;
                    const strokeDashoffset = -cumulativePercentage * 3.14;
                    cumulativePercentage += percentage;
                    
                    return (
                      <circle
                        key={item.key}
                        cx="96"
                        cy="96"
                        r="80"
                        fill="none"
                        stroke={item.color}
                        strokeWidth="16"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashoffset}
                        className="transition-all duration-1000"
                        style={{ 
                          filter: `drop-shadow(0 0 10px ${item.color}40)`,
                          animationDelay: `${index * 0.1}s` 
                        }}
                      />
                    );
                  });
                })()}
              </svg>
            </div>
          </div>
        </div>

        {/* Show remaining categories if any */}
        {pointsBreakdown.length > 5 && (
          <div className="mt-6 pt-6 border-t border-white/[0.08]">
            <details className="group">
              <summary className="cursor-pointer text-sm text-[#8AA5A2] hover:text-[#E8FDF2] transition-colors">
                View all {pointsBreakdown.length - 5} other categories
              </summary>
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
                {pointsBreakdown.slice(5).map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={item.key} className="p-3 bg-[#0A0E1B]/40 rounded-lg flex items-center gap-3">
                      <IconComponent className="w-4 h-4" style={{ color: item.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#8AA5A2] truncate">{item.category}</div>
                        <div className="text-sm font-medium text-[#E8FDF2]">{formatNumber(item.points)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          </div>
        )}
      </PremiumCard>

      {/* Activity Type Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PremiumCard>
          <PremiumContributionChart 
            data={githubActivityTypes} 
            type="bar" 
            title="GitHub Activity Types"
            color="#333333"
          />
        </PremiumCard>

        <PremiumCard>
          <PremiumContributionChart 
            data={xActivityTypes} 
            type="bar" 
            title="X Activity Types"
            color="#1DA1F2"
          />
        </PremiumCard>
      </div>

      {/* Total Growth Chart - Full width with gradient */}
      <PremiumCard className="overflow-hidden">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#15FF75]/5 to-[#2CF98E]/5 pointer-events-none"></div>
          <PremiumContributionChart 
            data={timeSeriesData.amPoints} 
            type="area" 
            title="Total AmPoints Growth Journey"
            color="#15FF75"
          />
        </div>
      </PremiumCard>

      {/* Recent Points History - Compact timeline */}
      <PremiumCard className="p-6">
        <h3 className="text-xl font-semibold text-[#E8FDF2] mb-6 flex items-center gap-2">
          <Clock className="w-6 h-6 text-[#8B5CF6]" />
          Recent Activity Timeline
        </h3>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#8B5CF6] to-transparent"></div>
          
          <div className="space-y-4">
            {displayHistory.length > 0 ? (
              displayHistory.map((entry, index) => {
                let details = '';
                try {
                  const parsed = JSON.parse(entry.details || '{}');
                  details = parsed.reason || parsed.message || 'Activity completed';
                } catch {
                  details = entry.details || 'Activity completed';
                }
                
                return (
                  <motion.div
                    key={index}
                    className="flex gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {/* Timeline dot */}
                    <div className="relative flex-shrink-0">
                      <div className="w-8 h-8 bg-[#0A0E1B] rounded-full border-2 border-[#8B5CF6] flex items-center justify-center">
                        <div className="w-2 h-2 bg-[#8B5CF6] rounded-full"></div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <div className="p-3 bg-[#0A0E1B]/40 rounded-xl border border-white/[0.08] hover:border-white/[0.12] transition-all">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-[#15FF75]">+{entry.pointsEarned}</span>
                              <span className="text-xs px-2 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded">
                                {entry.activityType}
                              </span>
                            </div>
                            <p className="text-sm text-[#8AA5A2]">{details}</p>
                          </div>
                          <span className="text-xs text-[#5A6B68] whitespace-nowrap">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="text-center py-8 text-[#8AA5A2]">
                No recent activity to display
              </div>
            )}
          </div>
        </div>
      </PremiumCard>
    </div>
  );
};

export default AnalyticsTab;